import React, { useEffect, useState } from 'react';
import { Button, Toggle } from '@/components';
import SideBarModal from '@/components/Modal/SideBarModal';
import {
  NotificationChannel,
  SLACK_WEBHOOK_PREFIX,
} from '@/services/api/notificationChannels';
import { FieldError, FieldHelper, FieldLabel, inputClass } from '@/components';

export interface NotificationChannelFormValues {
  channelName: string;
  /** Empty = leave the stored URL alone (the screen never sees it whole). */
  webhookURL: string;
  clearWebhook: boolean;
  isActive: boolean;
}

export const validateNotificationChannel = (
  values: NotificationChannelFormValues
): { webhookURL?: string } => {
  const url = values.webhookURL.trim();
  if (url && !url.startsWith(SLACK_WEBHOOK_PREFIX)) {
    return { webhookURL: `URL harus diawali ${SLACK_WEBHOOK_PREFIX}` };
  }
  return {};
};

export interface NotificationChannelModalProps {
  channel: NotificationChannel | null;
  onClose: () => void;
  onSubmit: (values: NotificationChannelFormValues) => Promise<void>;
  isSaving: boolean;
}

export const NotificationChannelModal: React.FC<
  NotificationChannelModalProps
> = ({ channel, onClose, onSubmit, isSaving }) => {
  const [values, setValues] = useState<NotificationChannelFormValues>({
    channelName: '',
    webhookURL: '',
    clearWebhook: false,
    isActive: true,
  });
  const [errors, setErrors] = useState<{ webhookURL?: string }>({});

  useEffect(() => {
    if (!channel) return;
    setErrors({});
    setValues({
      channelName: channel.channelName ?? '',
      webhookURL: '',
      clearWebhook: false,
      isActive: channel.isActive,
    });
  }, [channel]);

  const submit = async () => {
    const next = validateNotificationChannel(values);
    setErrors(next);
    if (next.webhookURL) return;
    await onSubmit(values);
  };

  const hasStoredURL = channel?.source === 'setting';

  return (
    <SideBarModal
      isOpen={Boolean(channel)}
      onClose={onClose}
      position="right"
      width="sm"
      header={
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Channel: {channel?.label}
          </h2>
          <p className="text-sm font-normal text-gray-500">
            Ke channel Slack mana keluarga alert ini dikirim.
          </p>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            size="small"
            onClick={onClose}
            disabled={isSaving}
          >
            Batal
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={submit}
            disabled={isSaving}
          >
            {isSaving ? 'Menyimpan…' : 'Simpan'}
          </Button>
        </div>
      }
    >
      {channel && (
        <div className="p-6 space-y-5">
          <p className="text-[13px] text-gray-600">{channel.description}</p>

          <div>
            <FieldLabel>Nama channel</FieldLabel>
            <input
              type="text"
              value={values.channelName}
              onChange={(e) =>
                setValues((prev) => ({ ...prev, channelName: e.target.value }))
              }
              placeholder="#ops-rent"
              maxLength={100}
              className={inputClass(false)}
            />
            <FieldHelper>Label saja, supaya tabel terbaca.</FieldHelper>
          </div>

          <div>
            <FieldLabel>Webhook URL baru</FieldLabel>
            <input
              type="text"
              value={values.webhookURL}
              onChange={(e) =>
                setValues((prev) => ({
                  ...prev,
                  webhookURL: e.target.value,
                  clearWebhook: false,
                }))
              }
              placeholder={`${SLACK_WEBHOOK_PREFIX}T…/B…/…`}
              disabled={values.clearWebhook}
              className={`${inputClass(Boolean(errors.webhookURL))} font-mono text-[13px]`}
            />
            <FieldError>{errors.webhookURL}</FieldError>
            <FieldHelper withIcon>
              {channel.webhookURLMasked
                ? `Sekarang: ${channel.webhookURLMasked} (${
                    channel.source === 'setting'
                      ? 'dari setting'
                      : 'dari env server'
                  }). Kosongkan untuk mempertahankan.`
                : 'Belum ada webhook di setting maupun env: alert keluarga ini tidak terkirim.'}
            </FieldHelper>
            {hasStoredURL && (
              <label className="mt-2 flex items-center gap-2 text-[13px] text-gray-700">
                <input
                  type="checkbox"
                  checked={values.clearWebhook}
                  onChange={(e) =>
                    setValues((prev) => ({
                      ...prev,
                      clearWebhook: e.target.checked,
                      webhookURL: e.target.checked ? '' : prev.webhookURL,
                    }))
                  }
                />
                Hapus URL dari setting
                {channel.hasEnvFallback
                  ? ' (kembali ke env server)'
                  : ' (tidak ada env, alert berhenti)'}
              </label>
            )}
          </div>

          <div className="rounded-lg border border-gray-200 p-3 flex items-start justify-between gap-3">
            <div>
              <p className="text-[14px] text-gray-900">Aktif</p>
              <p className="text-[13px] text-gray-500">
                Nonaktif = keluarga alert ini dibisukan sama sekali, termasuk
                fallback env. Pakai hanya kalau memang sengaja.
              </p>
            </div>
            <Toggle
              checked={values.isActive}
              onChange={(checked) =>
                setValues((prev) => ({ ...prev, isActive: checked }))
              }
              ariaLabel="Aktif"
            />
          </div>
          {channel.isActive && !values.isActive && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-[13px] text-amber-800">
              <i className="ri-alert-line mr-1"></i>
              Setelah disimpan, semua alert <b>{channel.label}</b> berhenti
              terkirim
              {channel.hasEnvFallback
                ? ' — termasuk webhook dari env server yang sekarang jadi fallback.'
                : '.'}
            </p>
          )}
        </div>
      )}
    </SideBarModal>
  );
};

export default NotificationChannelModal;
